/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Margin report — GET /re/reports/margins (project_id, from, to) → {rows, totals}.
 * net_revenue = net_price − dld_fee_borne − commission_total. CSV export is client side.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@mui/material';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import RePage, { FilterBar, LoadError, RePanel } from './components/RePage';
import ReTable from './components/ReTable';
import { RePicker } from './components/Pickers';
import { DateField, Num } from './components/Widgets';
import {
  downloadCsv, formatDate, formatMoney, formatPct, toNumber, useReGet,
} from './api';
import { defaultCurrency } from '../variant';

const MONEY_KEYS = ['list_price', 'discount_amount', 'net_price', 'dld_fee_borne', 'commission_total', 'net_revenue'];

export default function MarginReport() {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);
  const { data, loading, error, reload } = useReGet('/re/reports/margins', { project_id: projectId, from, to });
  const rows = useMemo(() => (data?.rows || []).map((r) => ({ ...r, id: r.booking_id })), [data]);

  // Prefer the server totals; fall back to summing rows.
  const totals = useMemo(() => {
    if (data?.totals && Object.keys(data.totals).length) return data.totals;
    const t = {};
    MONEY_KEYS.forEach((k) => { t[k] = rows.reduce((a, r) => a + (toNumber(r[k]) || 0), 0); });
    t.margin_pct = t.net_price ? (t.net_revenue / t.net_price) * 100 : null;
    return t;
  }, [data, rows]);

  const money = (k, label) => ({
    key: k, label, align: 'right',
    render: (r) => <Num>{formatMoney(r[k], defaultCurrency(), { decimals: 0 })}</Num>,
    footer: <Num bold>{formatMoney(totals[k], defaultCurrency(), { decimals: 0 })}</Num>,
  });

  const exportCsv = () => downloadCsv(
    `margin-report-${new Date().toISOString().slice(0, 10)}.csv`,
    [
      { key: 'booking_number', label: 'Booking' },
      { key: 'project_name', label: 'Project' },
      { key: 'unit_number', label: 'Unit' },
      { key: 'booking_date', label: 'Booking date' },
      ...MONEY_KEYS.map((k) => ({ key: k, label: k })),
      { key: 'margin_pct', label: 'margin_pct' },
    ],
    [...rows, { booking_number: 'TOTAL', ...totals }],
  );

  const marginColor = (m) => {
    const n = toNumber(m);
    if (n === null) return null;
    if (n < 85) return '#BA1A1A';
    if (n < 92) return '#8A5300';
    return '#1F8A56';
  };

  return (
    <RePage
      title="Margin report"
      subtitle="Net revenue per booking after DLD fees borne and broker commission"
      actions={(
        <MDButton variant="outlined" color="info" size="small" onClick={exportCsv} disabled={!rows.length}>
          <Icon>download</Icon>&nbsp;Export CSV
        </MDButton>
      )}
    >
      <FilterBar>
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
      </FilterBar>
      <LoadError error={error} onRetry={reload} />
      <RePanel p={0}>
        <ReTable
          loading={loading}
          rows={rows}
          emptyMessage="No bookings in this period."
          onRowClick={(r) => navigate(`/real-estate/bookings/${r.booking_id}`)}
          columns={[
            { key: 'booking_number', label: 'Booking', render: (r) => <strong>{r.booking_number}</strong>, footer: `Total (${rows.length})` },
            { key: 'project_name', label: 'Project' },
            { key: 'unit_number', label: 'Unit' },
            { key: 'booking_date', label: 'Date', render: (r) => formatDate(r.booking_date) },
            money('list_price', 'List price'),
            money('discount_amount', 'Discount'),
            money('net_price', 'Net price'),
            money('dld_fee_borne', 'DLD borne'),
            money('commission_total', 'Commission'),
            money('net_revenue', 'Net revenue'),
            {
              key: 'margin_pct', label: 'Margin', align: 'right',
              render: (r) => <Num bold color={marginColor(r.margin_pct)}>{formatPct(r.margin_pct)}</Num>,
              footer: <Num bold>{formatPct(totals.margin_pct)}</Num>,
            },
          ]}
        />
      </RePanel>
      <MDTypography variant="caption" color="text" component="p" mt={1}>
        Commission counts as a cost only where the tenant pays it (incl. VAT). Margin = net revenue ÷ net price.
      </MDTypography>
    </RePage>
  );
}
